/**
 * Komponent WiseTable - zaawansowana tabela z filtrowaniem, akcjami i stronicowaniem.
 * Stan jest wstrzykiwany przez WiseTableProvider (kontekst); komponenty potomne używają useWiseTable().
 */

import React, { FC } from 'react';
import classnames from 'classnames';
import { MaterialReactTable } from 'material-react-table';

import { Divider } from '@mui/material';

import { PageTitle } from 'components/controls';

import { WiseTableProvider, useWiseTable } from 'components/wise/WiseTable/context';
import TableTopToolbar from 'components/wise/WiseTable/components/TableTopToolbar';
import TableBottomBar from 'components/wise/WiseTable/components/TableBottomBar';
import CondensedTopBar from 'components/wise/WiseTable/components/CondensedTopBar';
import ColumnFilterPopper from 'components/wise/WiseTable/components/ColumnFilterPopper';
import WiseTableNoContextAction from 'components/wise/WiseTable/components/WiseTableNoContextAction';
import { IWiseTableProps } from 'components/wise/types';

import styles from 'theme/components/wise/WiseTable/WiseTable.module.scss';

/**
 * Wewnętrzny layout – wymaga WiseTableProvider (używa useWiseTable).
 */
const WiseTableInner: FC = () => {
  const {
    config,
    refetchTableData,
    noContextActions,
    materialReactTable,
    titleWithSetting,
    advancedFilters
  } = useWiseTable();

  const { isCondensed, showPlainTable, withAdvancedFilters, componentParams } = config;
  const showColumnFilterIcons = advancedFilters.showColumnFilterIcons;

  if (isCondensed) {
    return (
      <div className={classnames(styles.tableContainer, {[styles.plainTable]: showPlainTable})}>
        <CondensedTopBar />

        <div className={styles.tableWrapper}>
          <MaterialReactTable table={materialReactTable} />
        </div>

        {withAdvancedFilters && <ColumnFilterPopper />}

        <TableBottomBar />
      </div>
    );
  }

  return (
    <>
      <div className={styles.titleWrapper}>
        <PageTitle title={titleWithSetting} className={styles.pageTitle} />
        {noContextActions.length > 0 && (
          <div className={styles.titleActions}>
            {noContextActions.map((action, i) => (
              <WiseTableNoContextAction
                key={i}
                action={action}
                refetch={refetchTableData}
                componentParams={componentParams}
                index={i}
              />
            ))}
          </div>
        )}
      </div>

      <Divider sx={{ marginBottom: 2 }} />

      <div className={classnames(styles.tableContainer, showPlainTable && styles.noBoxShadow)}>
        <TableTopToolbar />

        <div className={styles.tableWrapper}>
          <MaterialReactTable table={materialReactTable} />
        </div>

        {withAdvancedFilters && showColumnFilterIcons && <ColumnFilterPopper />}

        <TableBottomBar />
      </div>
    </>
  );
};

/**
 * Komponent WiseTable – opakowuje drzewo w Provider i renderuje layout.
 */
const WiseTable: FC<IWiseTableProps> = (props) => {
  return (
    <WiseTableProvider props={props}>
      <div className={classnames(styles.componentWrapper, 'WiseTable', 'StylePath-WiseTable')}>
        <WiseTableInner />
      </div>
    </WiseTableProvider>
  );
};

export default WiseTable;
